import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import EmailPreview from '../EmailPreview';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';
import { StyledTypography } from '../styles/styles';
import ActivityLogDeltaChanges from './ActivityLogDeltaChanges';
import ContentDiffViewer, { extractContentDiffPayload } from './ContentDiffViewer';
import { formatActivityLogTimestamp } from './activityLogEntryUtils';
import { parseActivityLogExtendedInfo } from './activityLogPageEntryHelpers';

interface ActivityLogPageEntryExtendedPanelProps {
  log: any;
  tone: keyof typeof Palette;
  extendedInfoLoading: boolean;
  extendedInfoError: string | null;
  fetchedExtendedInfo: any;
  isContentDiffEvent: boolean;
  isEmailEvent: boolean;
  isEmailChangeEvent: boolean;
  isUserProfileUpdated: boolean;
  isEmailOpenedOrClicked: boolean;
  isPushNotificationEvent: boolean;
  emailExtendedInfo: any;
  loadingEmailSent: boolean;
  loadingEmailSentExtendedInfo: boolean;
  clickedLinkId: string | null;
  expanded: boolean;
  pushReceiptLoading: boolean;
  pushReceiptError: string | null;
  pushReceiptResult: any;
  onPushReceiptRequest: () => void;
}

const ActivityLogPageEntryExtendedPanel: React.FC<ActivityLogPageEntryExtendedPanelProps> = ({
  log,
  tone,
  extendedInfoLoading,
  extendedInfoError,
  fetchedExtendedInfo,
  isContentDiffEvent,
  isEmailEvent,
  isEmailChangeEvent,
  isUserProfileUpdated,
  isEmailOpenedOrClicked,
  isPushNotificationEvent,
  emailExtendedInfo,
  loadingEmailSent,
  loadingEmailSentExtendedInfo,
  clickedLinkId,
  expanded,
  pushReceiptLoading,
  pushReceiptError,
  pushReceiptResult,
  onPushReceiptRequest,
}) => {
  const navigate = useNavigate();

  let extendedInfo = fetchedExtendedInfo || log.extendedInfo;
  const { extendedInfo: parsedExtendedInfo, hasEmailChangeInProfileUpdate } =
    parseActivityLogExtendedInfo(extendedInfo, { isEmailChangeEvent, isUserProfileUpdated });
  extendedInfo = parsedExtendedInfo;

  const contentDiff = isContentDiffEvent ? extractContentDiffPayload(extendedInfo) : null;

  return (
    <Box
      sx={{
        flex: 1,
        borderLeft: `1px solid ${Palette[tone].border}`,
        pl: 2,
      }}
    >
      <StyledTypography tone={tone} fontSize="0.875rem" fontWeight={600} sx={{ mb: 1 }}>
        Extended Data:
      </StyledTypography>

      {extendedInfoLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <StyledTypography tone={tone} fontSize="0.75rem" sx={{ opacity: 0.7 }}>
            Loading extended data...
          </StyledTypography>
        </Box>
      ) : extendedInfoError ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StyledTypography tone={tone} fontSize="0.75rem" sx={{ color: '#d32f2f' }}>
            Error loading extended data: {extendedInfoError}
          </StyledTypography>
        </Box>
      ) : extendedInfo ? (
        <Box>
          {(extendedInfo.errorMessage || (log as any).errorMessage) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {extendedInfo.errorMessage || (log as any).errorMessage}
            </Alert>
          )}
          {contentDiff && log.originalContentPath && log.newContentPath && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Tooltip title="Show comparison against full content">
                <StyledButton
                  variant="outlined"
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => navigate(`/content-version/${log.id}`)}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Compare Full Content
                </StyledButton>
              </Tooltip>
            </Box>
          )}
          <Box
            sx={{
              maxHeight: isEmailEvent || isPushNotificationEvent ? 'none' : '300px',
              overflowY: isEmailEvent ? 'visible' : 'auto',
            }}
          >
            {contentDiff ? (
              <ContentDiffViewer diff={contentDiff} tone={tone} />
            ) : extendedInfo.delta ? (
              <ActivityLogDeltaChanges delta={extendedInfo.delta} tone={tone} />
            ) : isEmailEvent ? (
              <Box sx={{ mt: 1 }}>
                {(emailExtendedInfo?.errorMessage ||
                  extendedInfo?.errorMessage ||
                  (log as any).errorMessage) && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {emailExtendedInfo?.errorMessage ||
                      extendedInfo?.errorMessage ||
                      (log as any).errorMessage}
                  </Alert>
                )}
                {log.eventType === 'EMAIL_OPENED' && extendedInfo?.openCount !== undefined && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      backgroundColor: Palette[tone].background,
                      border: `1px solid ${Palette[tone].border}`,
                      borderRadius: '4px',
                    }}
                  >
                    <StyledTypography tone={tone} sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      Open Count: {extendedInfo.openCount}
                    </StyledTypography>
                  </Box>
                )}
                {isEmailOpenedOrClicked && (loadingEmailSent || loadingEmailSentExtendedInfo) ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                    <CircularProgress size={16} />
                    <StyledTypography tone={tone} fontSize="0.75rem" sx={{ opacity: 0.7 }}>
                      Loading email content...
                    </StyledTypography>
                  </Box>
                ) : emailExtendedInfo?.to &&
                  emailExtendedInfo?.subject &&
                  emailExtendedInfo?.renderedEmail?.html ? (
                  <EmailPreview
                    email={{
                      to: emailExtendedInfo.to,
                      subject: emailExtendedInfo.subject,
                      renderedEmail: {
                        html: emailExtendedInfo.renderedEmail.html,
                        text: emailExtendedInfo.renderedEmail.text,
                      },
                    }}
                    highlightedLinkId={clickedLinkId}
                  />
                ) : (
                  <StyledTypography tone={tone} fontSize="0.75rem" sx={{ opacity: 0.7 }}>
                    Email content not available
                  </StyledTypography>
                )}
              </Box>
            ) : isEmailChangeEvent || hasEmailChangeInProfileUpdate ? (
              <Box sx={{ mt: 1 }}>
                {extendedInfo?.email?.from && extendedInfo?.email?.to ? (
                  <Box>
                    <ActivityLogDeltaChanges
                      delta={{
                        email: {
                          from: extendedInfo.email.from,
                          to: extendedInfo.email.to,
                        },
                      }}
                      tone={tone}
                    />
                    {(log.eventType === 'EMAIL_CHANGE_PENDING' || hasEmailChangeInProfileUpdate) &&
                      extendedInfo?.expiry && (
                        <Box sx={{ mt: 1, p: 1 }}>
                          <StyledTypography tone={tone} sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
                            <strong>Status:</strong> Pending
                          </StyledTypography>
                          <StyledTypography tone={tone} sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
                            <strong>Expires:</strong>{' '}
                            {format(new Date(extendedInfo.expiry), 'MMM dd, yyyy HH:mm:ss')}
                          </StyledTypography>
                        </Box>
                      )}
                    {hasEmailChangeInProfileUpdate && !extendedInfo?.expiry && (
                      <Box sx={{ mt: 1, p: 1 }}>
                        <StyledTypography tone={tone} sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
                          <strong>Status:</strong> Pending
                        </StyledTypography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <StyledTypography tone={tone} sx={{ fontSize: '0.875rem', opacity: 0.7 }}>
                    Email change information not available
                  </StyledTypography>
                )}
              </Box>
            ) : isPushNotificationEvent && (extendedInfo?.title || extendedInfo?.body) ? (
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: Palette[tone].background,
                    border: `1px solid ${Palette[tone].border}`,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    maxWidth: 360,
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/favicon.png"
                    alt=""
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      objectFit: 'contain',
                      backgroundColor: Palette[tone].background,
                      p: 0.5,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {extendedInfo.title && (
                      <StyledTypography
                        tone={tone}
                        sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.3 }}
                      >
                        {extendedInfo.title}
                      </StyledTypography>
                    )}
                    {extendedInfo.body && (
                      <StyledTypography
                        tone={tone}
                        sx={{ fontSize: '0.8125rem', opacity: 0.9, mt: 0.25, lineHeight: 1.4 }}
                      >
                        {extendedInfo.body}
                      </StyledTypography>
                    )}
                    <StyledTypography
                      tone={tone}
                      sx={{ fontSize: '0.6875rem', opacity: 0.6, mt: 0.5 }}
                    >
                      {formatActivityLogTimestamp(log.eventTimestamp)}
                    </StyledTypography>
                  </Box>
                </Box>
                {log.eventType === 'PUSH_NOTIFICATION_SENT' && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={pushReceiptLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPushReceiptRequest();
                      }}
                    >
                      {pushReceiptLoading ? 'Loading…' : 'Get receipt status'}
                    </Button>
                    {pushReceiptError && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {pushReceiptError}
                      </Alert>
                    )}
                    {pushReceiptResult && (
                      <Box sx={{ mt: 1 }}>
                        <StyledTypography
                          tone={tone}
                          fontSize="0.75rem"
                          fontWeight={600}
                          sx={{ mb: 0.5 }}
                        >
                          Receipt status (raw JSON):
                        </StyledTypography>
                        <pre
                          style={{
                            fontSize: '0.75rem',
                            fontFamily: 'monospace',
                            backgroundColor: Palette[tone].background,
                            padding: '8px',
                            borderRadius: '4px',
                            border: `1px solid ${Palette[tone].border}`,
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            maxHeight: 320,
                            overflow: 'auto',
                          }}
                        >
                          {JSON.stringify(pushReceiptResult, null, 2)}
                        </pre>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <pre
                style={{
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  backgroundColor: Palette[tone].background,
                  padding: '8px',
                  borderRadius: '4px',
                  border: `1px solid ${Palette[tone].border}`,
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(extendedInfo, null, 2)}
              </pre>
            )}
          </Box>
        </Box>
      ) : isContentDiffEvent && expanded && !extendedInfo ? (
        <StyledTypography tone={tone} fontSize="0.75rem" sx={{ color: 'text.secondary' }}>
          Content diff not available yet.
        </StyledTypography>
      ) : isContentDiffEvent ? (
        <StyledTypography tone={tone} fontSize="0.75rem" sx={{ opacity: 0.7 }}>
          Diff not available yet.
        </StyledTypography>
      ) : log.extendedInfoPath ? (
        <StyledTypography tone={tone} fontSize="0.75rem" sx={{ opacity: 0.7 }}>
          No extended data available
        </StyledTypography>
      ) : (
        <StyledTypography tone={tone} fontSize="0.75rem" sx={{ opacity: 0.7 }}>
          No extended data path provided
        </StyledTypography>
      )}
    </Box>
  );
};

export default ActivityLogPageEntryExtendedPanel;
